/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/common/PortalTooltip.tsx
 * PURPOSE: Tooltip hover/focus render qua React portal + position:fixed theo rect
 *          của phần tử kích hoạt, tránh bị container overflow-x/y:auto cắt đứng
 *          (ISS-MGR-3 — cùng họ root cause với PortalMenu F-6/F-7/F-8, nhưng
 *          trigger cũ là CSS `[data-tip]:hover::after`, không thoát được vùng
 *          overflow của bảng vì pseudo-element vẫn nằm trong cây layout cha).
 *          Tự lật xuống dưới khi sát mép trên viewport, tự kẹp trái/phải khi
 *          anchor nằm sát mép ngang viewport (VD icon ⓘ ở cột cuối cùng bảng —
 *          phát hiện 2026-08-09 khi vá thêm ManagerSchedulePage: bubble maxWidth
 *          240px canh giữa lên anchor tràn 61.5px ra ngoài mép phải màn hình).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-09
 * OWNER: @coder_frontend
 *
 * @technical-architecture
 * - Pattern: createPortal vào document.body + fixed positioning, đo offsetHeight/
 *   offsetWidth sau render đầu để quyết hướng mở dọc (lên mặc định, xuống khi
 *   không đủ chỗ) + kẹp toạ độ ngang trong [8px, viewport.width - 8px] — cùng kỹ
 *   thuật đo/lật của PortalMenu.tsx, khác ở chỗ trigger là hover/focus
 *   (mouseenter/mouseleave/focus/blur) thay vì click-toggle.
 * - Giữ nguyên style trực quan của CSS `::after` cũ (nền --nqh-gray-900, chữ
 *   --nqh-bg-surface, bo góc --nqh-radius-md) bằng cách tham chiếu thẳng cùng
 *   design token qua inline style var(...).
 *
 * @cross-references
 * - Issue: docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md ISS-MGR-3
 * - Pattern tiền lệ: frontend/src/components/common/PortalMenu.tsx (F-6/F-7/F-8)
 * - Consumers: ManagerAttendancePage.tsx · ManagerStaffPage.tsx
 * ===============================================================================
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface PortalTooltipProps {
  /** Nội dung tooltip (trước đây là attr `data-tip`) */
  text: string;
  /** Phần tử kích hoạt hover (icon ⓘ, tên nhân viên, cờ...) */
  children: React.ReactNode;
  className?: string;
  ariaLabel?: string;
  tabIndex?: number;
}

export const PortalTooltip: React.FC<PortalTooltipProps> = ({
  text,
  children,
  className,
  ariaLabel,
  tabIndex,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const show = () => {
    if (anchorRef.current) setRect(anchorRef.current.getBoundingClientRect());
  };
  const hide = () => setRect(null);

  return (
    <span
      ref={anchorRef}
      className={className}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {rect && createPortal(<TooltipBubble text={text} rect={rect} />, document.body)}
    </span>
  );
};

const EDGE_MARGIN = 8;

const TooltipBubble: React.FC<{ text: string; rect: DOMRect }> = ({ text, rect }) => {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [flipDown, setFlipDown] = useState(false);
  // 2026-09-07 (vòng 2 sync prototype `sc011-build-prototype.html` `showTip()`
  // dòng ~756-772, wireframe không đặc tả chi tiết này — chỉ prototype cho):
  // "mép trái tooltip thẳng hàng với chữ đứng trước icon, KHÔNG căn giữa" —
  // trước đây `centerX` canh giữa bubble LÊN CHÍNH icon (span bọc children
  // của PortalTooltip chỉ là icon, không gồm label đứng trước). Đổi neo trái
  // = `rect.left` (mép trái icon — icon luôn sát ngay sau label, gap nhỏ, nên
  // xấp xỉ đúng "ngay sau chữ" mà không cần với ra `parentElement` — tránh
  // giả định cấu trúc DOM cha ở mọi nơi dùng component dùng-chung này).
  const [left, setLeft] = useState(rect.left);

  // Đo kích thước thật sau render đầu:
  // - lật xuống nếu mở lên sẽ tràn đỉnh viewport
  // - kẹp `left` trong [margin, viewport.width - margin - bubbleWidth] nếu icon
  //   nằm sát mép phải màn hình (VD icon cột cuối cùng bảng) — bubble rộng cố
  //   định (maxWidth 240) neo trái nên có thể tràn phải nếu không kẹp.
  useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!el) return;
    setFlipDown(rect.top - 6 - el.offsetHeight < 0);
    const clamped = Math.min(
      Math.max(rect.left, EDGE_MARGIN),
      window.innerWidth - EDGE_MARGIN - el.offsetWidth
    );
    setLeft(clamped);
  }, [rect]);

  const pos: React.CSSProperties = flipDown
    ? { top: rect.bottom + 6 }
    : { bottom: window.innerHeight - rect.top + 6 };

  return (
    <div
      ref={bubbleRef}
      role="tooltip"
      style={{
        position: "fixed",
        left,
        ...pos,
        zIndex: "var(--nqh-z-tooltip)" as unknown as number,
        background: "var(--nqh-gray-900)",
        color: "var(--nqh-bg-surface)",
        padding: "var(--nqh-space-1) var(--nqh-space-2)",
        borderRadius: "var(--nqh-radius-md)",
        fontSize: "var(--nqh-text-xs)",
        fontWeight: "var(--nqh-weight-normal)",
        lineHeight: 1.4,
        whiteSpace: "normal",
        width: "max-content",
        maxWidth: 240,
        boxShadow: "var(--nqh-shadow-md)",
        pointerEvents: "none",
      }}
    >
      {text}
    </div>
  );
};

export default PortalTooltip;
